/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const Placeholder = ({ label, value = "À compléter" }) => (
  <span className="inline-flex items-baseline gap-2">
    <strong className="text-ink">{label} :</strong>
    <span className="text-ink/60 italic bg-cyan/5 border border-cyan/20 px-2 py-0.5 text-sm">{value}</span>
  </span>
);

export const LegalPage = ({ title, subtitle, updatedAt = "Février 2026", sections }) => (
  <div className="min-h-screen bg-paper">
    <Navbar />
    <main id="main" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink mb-10 link-under">
          <ArrowLeft size={14} aria-hidden="true" /> Retour à l'accueil
        </Link>
        <p className="font-mono-label text-cyan-600 flex items-center">
          <span className="rule bg-cyan" /> Informations légales
        </p>
        <h1 className="mt-6 font-display text-5xl md:text-6xl text-ink tracking-tight leading-[1.05]">{title}</h1>
        {subtitle && <p className="mt-6 text-ink/70 leading-relaxed">{subtitle}</p>}
        <p className="mt-3 text-xs text-ink/50">Dernière mise à jour : {updatedAt}</p>
        <div className="mt-12 space-y-12">
          {sections.map((s) => {
            const secId = s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
            return (
              <section key={secId} aria-labelledby={`sec-${secId}`}>
                <h2 id={`sec-${secId}`} className="font-display text-2xl text-ink">{s.title}</h2>
                <div className="mt-4 text-ink/75 leading-relaxed space-y-3">
                  {s.paragraphs.map((p, j) => {
                    const key = `${secId}-p-${j}`;
                    if (typeof p === "string") {
                      return <p key={key}>{p}</p>;
                    }
                    return <div key={key}>{p}</div>;
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {/* Cross-links */}
        <nav aria-label="Autres pages légales" className="mt-16 pt-8 border-t border-ink/10 flex flex-wrap gap-4 text-sm">
          <Link to="/mentions-legales" className="text-ink/70 hover:text-ink link-under">Mentions légales</Link>
          <Link to="/politique-confidentialite" className="text-ink/70 hover:text-ink link-under">Politique de confidentialité</Link>
          <Link to="/cgv" className="text-ink/70 hover:text-ink link-under">CGV</Link>
          <Link to="/cgu" className="text-ink/70 hover:text-ink link-under">CGU</Link>
          <Link to="/cookies" className="text-ink/70 hover:text-ink link-under">Cookies</Link>
        </nav>
      </div>
    </main>
    <Footer />
  </div>
);

/* ============ MENTIONS LÉGALES ============ */
export const MentionsLegales = () => (
  <LegalPage
    title="Mentions légales"
    subtitle="Conformément aux articles 6-III et 19 de la Loi n°2004-575 du 21 juin 2004 pour la Confiance dans l'Économie Numérique (LCEN)."
    sections={[
      { title: "1. Éditeur du site", paragraphs: [
        <ul className="space-y-2 list-none pl-0">
          <li><Placeholder label="Nom de l'entreprise" value="WebUP (à compléter)" /></li>
          <li><Placeholder label="Forme juridique" /></li>
          <li><Placeholder label="Adresse du siège" value="Marssac-sur-Tarn (81150) — à compléter" /></li>
          <li><Placeholder label="Numéro SIRET" /></li>
          <li><Placeholder label="Numéro TVA intracommunautaire" /></li>
          <li><Placeholder label="Responsable de la publication" value="Marius Nougaillon" /></li>
          <li><Placeholder label="Email de contact" value="contact.agency.webup@gmail.com" /></li>
          <li><Placeholder label="Téléphone" value="06 46 79 25 54" /></li>
        </ul>,
        "Ces informations seront mises à jour dès l'immatriculation officielle de l'entreprise.",
      ]},
      { title: "2. Hébergeur", paragraphs: [
        <ul className="space-y-2 list-none pl-0">
          <li><Placeholder label="Nom de l'hébergeur" /></li>
          <li><Placeholder label="Adresse" /></li>
          <li><Placeholder label="Téléphone" /></li>
        </ul>,
      ]},
      { title: "3. Propriété intellectuelle", paragraphs: [
        "L'ensemble des contenus présents sur le site webup-tarn.fr (textes, images, logos, identité visuelle, structure, code source) est la propriété exclusive de WebUP, sauf mention contraire. Toute reproduction, représentation ou diffusion, totale ou partielle, sans autorisation écrite préalable, est strictement interdite et constitue une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle.",
      ]},
      { title: "4. Crédits", paragraphs: [
        "Création & développement : Marius Nougaillon — WebUP. Photothèque partiellement issue de banques d'images libres de droits (Unsplash, Pexels).",
      ]},
      { title: "5. Limitation de responsabilité", paragraphs: [
        "L'éditeur s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site, dont il se réserve le droit de modifier à tout moment le contenu, sans préavis. L'éditeur ne saurait être tenu responsable des erreurs, omissions ou de l'absence de disponibilité des informations et services proposés.",
      ]},
      { title: "6. Loi applicable", paragraphs: [
        "Le présent site et ses mentions légales sont soumis au droit français. Tout litige relatif à leur exécution ou interprétation relèvera de la compétence exclusive des tribunaux d'Albi (81).",
      ]},
    ]}
  />
);

/* ============ POLITIQUE DE CONFIDENTIALITÉ ============ */
export const PolitiqueConfidentialite = () => (
  <LegalPage
    title="Politique de confidentialité"
    subtitle="WebUP s'engage à respecter la vie privée de ses visiteurs et clients, conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et à la loi Informatique et Libertés modifiée."
    sections={[
      { title: "1. Responsable du traitement", paragraphs: [
        <ul className="space-y-2 list-none pl-0">
          <li><Placeholder label="Responsable de traitement" value="Marius Nougaillon — WebUP" /></li>
          <li><Placeholder label="Adresse" value="Marssac-sur-Tarn (81150)" /></li>
          <li><Placeholder label="Email RGPD" value="contact.agency.webup@gmail.com" /></li>
        </ul>,
      ]},
      { title: "2. Données personnelles collectées", paragraphs: [
        "Nous collectons uniquement les données strictement nécessaires à la relation commerciale :",
        <ul className="list-disc pl-6 space-y-1 marker:text-cyan-500">
          <li>Identité : nom, prénom, entreprise ;</li>
          <li>Coordonnées : email, téléphone, ville ;</li>
          <li>Informations projet : secteur d'activité, budget estimé, description du projet, services souhaités ;</li>
          <li>Données de navigation techniques strictement nécessaires (cookie d'authentification pour l'espace administrateur uniquement).</li>
        </ul>,
      ]},
      { title: "3. Finalités du traitement", paragraphs: [
        "Vos données sont utilisées exclusivement pour :",
        <ul className="list-disc pl-6 space-y-1 marker:text-cyan-500">
          <li>répondre à votre demande de devis ou de rendez-vous ;</li>
          <li>établir et exécuter les prestations contractuelles ;</li>
          <li>vous transmettre les informations liées à votre projet ;</li>
          <li>respecter nos obligations légales et comptables.</li>
        </ul>,
      ]},
      { title: "4. Base légale", paragraphs: [
        "Les traitements reposent sur : (a) l'exécution de mesures précontractuelles à votre demande (art. 6.1.b RGPD), (b) l'exécution du contrat, (c) le respect des obligations légales, ou (d) votre consentement pour certains traitements (cookies non essentiels).",
      ]},
      { title: "5. Durée de conservation", paragraphs: [
        <ul className="list-disc pl-6 space-y-1 marker:text-cyan-500">
          <li>Prospects sans suite : <strong>3 ans</strong> à compter du dernier contact ;</li>
          <li>Clients : <strong>10 ans</strong> à compter de la fin de la relation contractuelle (obligations comptables) ;</li>
          <li>Cookie d'authentification : <strong>365 jours</strong> maximum.</li>
        </ul>,
      ]},
      { title: "6. Destinataires des données", paragraphs: [
        "Vos données sont accessibles uniquement à WebUP. Elles ne sont jamais vendues, louées ou cédées à des tiers. Certaines données peuvent être traitées par nos sous-traitants techniques (hébergement, envoi d'emails) dans le cadre d'un contrat conforme à l'article 28 du RGPD.",
      ]},
      { title: "7. Transfert hors UE", paragraphs: [
        "Nos serveurs sont situés dans l'Union Européenne. Aucun transfert de données hors UE n'est effectué sans garanties adéquates (clauses contractuelles types de la Commission Européenne).",
      ]},
      { title: "8. Sécurité", paragraphs: [
        "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées : chiffrement HTTPS/TLS, hachage bcrypt des mots de passe administrateurs, tokens JWT signés, accès restreint aux bases de données, sauvegardes régulières et journalisation.",
      ]},
      { title: "9. Vos droits", paragraphs: [
        "Conformément au RGPD, vous disposez des droits suivants :",
        <ul className="list-disc pl-6 space-y-1 marker:text-cyan-500">
          <li><strong>Droit d'accès</strong> à vos données personnelles ;</li>
          <li><strong>Droit de rectification</strong> des données inexactes ;</li>
          <li><strong>Droit à l'effacement</strong> (« droit à l'oubli ») ;</li>
          <li><strong>Droit à la limitation</strong> du traitement ;</li>
          <li><strong>Droit à la portabilité</strong> de vos données ;</li>
          <li><strong>Droit d'opposition</strong> au traitement ;</li>
          <li><strong>Droit de retirer votre consentement</strong> à tout moment.</li>
        </ul>,
        "Ces droits peuvent être exercés en écrivant à contact.agency.webup@gmail.com. Nous répondons dans un délai maximum d'un mois.",
      ]},
      { title: "10. Réclamation", paragraphs: [
        "Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous pouvez adresser une réclamation à la CNIL — 3 Place de Fontenoy, TSA 80715, 75334 PARIS CEDEX 07 — ou en ligne sur www.cnil.fr.",
      ]},
      { title: "11. Contact du DPO", paragraphs: [
        "Pour toute question relative à cette politique ou à vos données : contact.agency.webup@gmail.com.",
      ]},
    ]}
  />
);

/* ============ COOKIES ============ */
export const CookiesPage = () => (
  <LegalPage
    title="Politique cookies"
    subtitle="Information sur l'utilisation des cookies et traceurs sur le site webup-tarn.fr, conformément aux recommandations de la CNIL."
    sections={[
      { title: "1. Qu'est-ce qu'un cookie ?", paragraphs: [
        "Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, smartphone) lors de la visite d'un site. Il permet, entre autres, de mesurer l'audience, faciliter la navigation, personnaliser le contenu ou maintenir une session authentifiée.",
      ]},
      { title: "2. Cookies utilisés sur webup-tarn.fr", paragraphs: [
        <div className="border border-ink/10 divide-y divide-ink/10">
          {[
            ["access_token", "Essentiel", "Authentification de l'espace administrateur", "365 jours"],
            ["webup_cookie_consent", "Essentiel", "Mémorisation de vos choix cookies", "12 mois"],
            ["webup_token", "Essentiel", "Fallback d'authentification (localStorage)", "365 jours"],
          ].map(([n, t, d, dur]) => (
            <div key={n} className="grid grid-cols-4 gap-4 p-3 text-sm">
              <span className="font-mono text-ink">{n}</span>
              <span className="text-cyan-600 font-medium">{t}</span>
              <span className="text-ink/70">{d}</span>
              <span className="text-ink/60">{dur}</span>
            </div>
          ))}
        </div>,
        "Aucun cookie publicitaire, de profilage ou de mesure d'audience tiers n'est déposé sur le site public. Les cookies d'authentification sont strictement nécessaires et exemptés de consentement préalable (délibération CNIL 2020-091).",
      ]},
      { title: "3. Gestion et retrait du consentement", paragraphs: [
        "Vous pouvez à tout moment modifier vos préférences via la bannière de consentement affichée en bas de page. Vous pouvez également configurer votre navigateur pour refuser tout ou partie des cookies (Chrome, Firefox, Safari, Edge disposent tous d'options de gestion). Le refus des cookies non essentiels n'affecte pas l'utilisation du site.",
      ]},
      { title: "4. Durée de conservation du consentement", paragraphs: [
        "Votre choix (accepter, refuser, personnaliser) est conservé pendant 12 mois. Au-delà, la bannière vous sera à nouveau présentée.",
      ]},
    ]}
  />
);

/* ============ CGV ============ */
export const CGV = () => (
  <LegalPage
    title="Conditions générales de vente"
    subtitle="Les présentes CGV régissent l'ensemble des prestations fournies par WebUP à ses clients professionnels."
    sections={[
      { title: "1. Objet", paragraphs: [
        "Les présentes Conditions Générales de Vente s'appliquent à toutes les prestations de création, refonte, référencement (SEO), maintenance, automatisation, création de fiches Google Business Profile, QR Codes professionnels et intégrations d'intelligence artificielle proposées par WebUP.",
      ]},
      { title: "2. Prestations", paragraphs: [
        "WebUP propose notamment : création de sites vitrines et e-commerce, refonte de sites existants, référencement local dans le Tarn, création et optimisation de fiches Google Business Profile, chatbots IA, agents vocaux IA, formulaires de devis, prise de rendez-vous en ligne, maintenance mensuelle. Le détail exact des prestations retenues figure au devis signé par le client.",
      ]},
      { title: "3. Tarifs", paragraphs: [
        "Les prix sont exprimés en euros. Le Pack Site Web Professionnel démarre à 1 000 €. La maintenance mensuelle est facturée 30 €/mois. Les prestations d'automatisation et d'IA sont chiffrées sur devis. Les tarifs peuvent être révisés annuellement.",
        <div>
          <Placeholder label="Assujettissement à la TVA" />
        </div>,
      ]},
      { title: "4. Devis et commande", paragraphs: [
        "Toute prestation fait l'objet d'un devis détaillé, gratuit et sans engagement. La commande est ferme dès réception du devis signé par le client et du versement de l'acompte prévu. Le devis a une durée de validité de 30 jours.",
      ]},
      { title: "5. Modalités de paiement", paragraphs: [
        <ul className="list-disc pl-6 space-y-1 marker:text-cyan-500">
          <li><strong>30 %</strong> d'acompte à la signature du devis ;</li>
          <li><strong>70 %</strong> du solde à la livraison du projet (mise en ligne effective) ;</li>
          <li>Paiement par virement bancaire ;</li>
          <li>La maintenance mensuelle est prélevée ou facturée à date fixe chaque mois.</li>
        </ul>,
      ]},
      { title: "6. Retard de paiement", paragraphs: [
        "En cas de retard, des pénalités égales à trois fois le taux d'intérêt légal en vigueur pourront être appliquées, ainsi qu'une indemnité forfaitaire de 40 € pour frais de recouvrement (articles L.441-10 et D.441-5 du Code de commerce).",
      ]},
      { title: "7. Délais", paragraphs: [
        "Les délais de livraison sont indiqués à titre indicatif dans le devis. WebUP s'engage à respecter ces délais sous réserve de la fourniture des contenus et validations par le client. Aucun retard imputable au client ne peut engager la responsabilité de WebUP.",
      ]},
      { title: "8. Propriété intellectuelle", paragraphs: [
        "Le client devient propriétaire des éléments graphiques et du contenu de son site après paiement intégral. Les codes sources et briques techniques spécifiques développés restent la propriété de WebUP, qui accorde au client une licence d'utilisation non exclusive et non transférable.",
      ]},
      { title: "9. Responsabilité", paragraphs: [
        "WebUP s'engage à exécuter les prestations avec le soin et la diligence d'un professionnel avisé. La responsabilité de WebUP est strictement limitée au montant hors taxes de la prestation concernée. WebUP ne peut être tenu responsable des dommages indirects (perte de chiffre d'affaires, perte de données non liée à sa prestation, etc.).",
      ]},
      { title: "10. Résiliation", paragraphs: [
        "Le contrat de maintenance peut être résilié par l'une ou l'autre des parties moyennant un préavis de 30 jours notifié par email ou courrier recommandé. Les prestations ponctuelles engagées demeurent dues.",
      ]},
      { title: "11. Confidentialité", paragraphs: [
        "Chaque partie s'engage à conserver la confidentialité des informations échangées dans le cadre de l'exécution du contrat.",
      ]},
      { title: "12. Droit applicable et juridiction", paragraphs: [
        "Les présentes CGV sont régies par le droit français. À défaut de résolution amiable, tout litige relèvera de la compétence exclusive des tribunaux d'Albi (81).",
      ]},
    ]}
  />
);

/* ============ CGU ============ */
export const CGU = () => (
  <LegalPage
    title="Conditions générales d'utilisation"
    subtitle="Les présentes CGU définissent les modalités d'accès et d'utilisation du site webup-tarn.fr."
    sections={[
      { title: "1. Objet", paragraphs: [
        "Les présentes CGU ont pour objet de définir les modalités et conditions dans lesquelles WebUP met à disposition son site internet, ainsi que les modalités selon lesquelles les utilisateurs y accèdent et l'utilisent.",
      ]},
      { title: "2. Accès au site", paragraphs: [
        "Le site est accessible gratuitement à tout utilisateur disposant d'un accès à internet. Les frais d'accès et d'utilisation du réseau restent à la charge de l'utilisateur. WebUP se réserve le droit d'interrompre l'accès pour des raisons techniques ou de maintenance, sans que sa responsabilité ne puisse être engagée.",
      ]},
      { title: "3. Utilisation du site", paragraphs: [
        "L'utilisateur s'engage à utiliser le site conformément à sa destination et à ne pas porter atteinte à son fonctionnement. Sont notamment interdits : toute tentative d'intrusion, de collecte automatisée de données, d'atteinte à la sécurité, ou d'utilisation frauduleuse des formulaires.",
      ]},
      { title: "4. Responsabilité", paragraphs: [
        "WebUP met tout en œuvre pour offrir aux utilisateurs des informations et outils fiables et à jour. Toutefois, WebUP ne saurait être tenu responsable des erreurs, d'une absence de disponibilité des fonctionnalités ou de la présence de virus sur son site. L'utilisateur est seul responsable de l'utilisation qu'il fait des informations fournies.",
      ]},
      { title: "5. Propriété intellectuelle", paragraphs: [
        "La structure générale du site, ainsi que les textes, graphiques, images, sons et vidéos qui le composent, sont la propriété exclusive de WebUP. Toute reproduction ou représentation, totale ou partielle, sans autorisation expresse, est prohibée.",
      ]},
      { title: "6. Données personnelles", paragraphs: [
        <span>Les données transmises via les formulaires du site sont traitées conformément à notre <Link to="/politique-confidentialite" className="link-under text-ink">Politique de confidentialité</Link>.</span>,
      ]},
      { title: "7. Cookies", paragraphs: [
        <span>Le site utilise des cookies strictement nécessaires. Consultez notre <Link to="/cookies" className="link-under text-ink">Politique cookies</Link> pour plus d'informations et gérer vos préférences.</span>,
      ]},
      { title: "8. Liens hypertextes", paragraphs: [
        "Le site peut contenir des liens vers des sites tiers. WebUP n'exerce aucun contrôle sur leur contenu et ne saurait être tenu responsable des dommages liés à leur consultation.",
      ]},
      { title: "9. Modification des CGU", paragraphs: [
        "WebUP se réserve le droit de modifier les présentes CGU à tout moment. Les modifications entrent en vigueur dès leur publication sur le site.",
      ]},
      { title: "10. Contact", paragraphs: [
        "Pour toute question relative aux présentes CGU : contact.agency.webup@gmail.com — 06 46 79 25 54.",
      ]},
    ]}
  />
);
